import type {
  MultipleChoiceQuestion,
  RatingQuestion,
  TextQuestion,
} from '@shared/domain/form';
import type { IntelligenceRequest } from '@shared/domain/intelligenceRequest/IntelligenceRequest';
import type { BasicReview } from '@shared/domain/review';
import type { RatingsInput, RatingValues } from '../../api/api';
import { Box, Grid } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '@/shared/contexts/UserContext';
import { QuestionType } from '@shared/domain/form';
import { IntelligenceRequestStatus } from '@shared/domain/intelligenceRequest/IntelligenceRequestStatus';
import { ReviewDepth, ReviewStep } from '@shared/domain/review';
import { useIntelligenceRequests } from '@shared/hooks/useIntelligenceRequests';
import { useApi } from '@shared/services/api';
import { P } from '@shared/styles';
import { MOBILE_BREAKPOINT } from '@shared/styles/Breakpoints';
import { CenteredDiv } from '@shared/styles/FormStyles';
import { useExtendState } from '@shared/utils/ExtendState';
import {
  createBasicReview,
  createBasicReviewRatings,
  createCompanyFromReview,
} from '../../api/api';
import { useReviewContext } from '../../context/ReviewContext';
import { BASIC_REVIEW_FORM_FIELDS } from '../../data';
import { getReviewStepName } from '../../helpers/ReviewStepNames';
import useValidation from '../../hooks/useValidation';
import { SaveBar, SaveButton } from '../../styles/review.styles';
import ActiveReviewIntelligenceRequests from '../ActiveReviewIntelligenceRequests';
import AddCompanyForm from '../AddCompanyForm';
import { BaseReviewForm } from '../BaseReviewForm';
import MultipleChoiceReviewQuestion from '../question_types/ReviewMultipleChoiceQuestion';
import ReviewRatingQuestion from '../question_types/ReviewRatingQuestion';
import ReviewTextArea from '../question_types/ReviewTextQuestion';
import {
  InfoBar,
  SectionSubtext,
  SectionTitle,
  TextContainer,
} from './ReviewForm.styles';

function toNumeric(val: any): number {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
}

function mapRatingsArray(
  ratings?: Array<{ type?: string; rating?: string | number }>,
): RatingValues {
  const out: RatingValues = {
    transparency: 0,
    clarity: 0,
    responsiveness: 0,
    understanding: 0,
  };
  if (!Array.isArray(ratings))
    return out;
  for (const r of ratings) {
    const key = (r?.type || '').toString().toLowerCase();
    if (key in out) {
      // @ts-expect-error index by known keys
      out[key] = toNumeric(r?.rating);
    }
  }
  return out;
}

interface BasicReviewTypeProps {
  companyId?: number;
  companyName?: string;
  cancelReview?: () => void;
  handleReviewSuccess?: (showSnackbar: boolean, redirectPath?: string) => void;
  handleEditSuccess?: () => void;
  setRfp?: (rfp: boolean) => void;
  isAddCompany?: boolean;
  editMode?: boolean;
  defaultValues?: Partial<BasicReview & RatingValues>;
}

function getDisabledOptions(
  questionName: string,
  selectedRequest: IntelligenceRequest | null,
): string[] | undefined {
  if (questionName === 'dealArea') {
    return selectedRequest?.dealArea && selectedRequest.dealArea.length > 0
      ? selectedRequest.dealArea
      : undefined;
  }
  if (questionName === 'dealClose') {
    const outcome = selectedRequest?.dealOutcome;
    return outcome ? [outcome] : undefined;
  }
  return undefined;
}

const BasicReviewForm: React.FC<BasicReviewTypeProps> = ({
  companyId,
  companyName,
  setRfp,
  isAddCompany,
  cancelReview,
  handleReviewSuccess,
  editMode,
  defaultValues,
  handleEditSuccess,
}) => {
  const { fetchFilteredReviews, currentReview, setReviewId, setReviewStep, reviewId }
    = useReviewContext();
  const { user } = useContext(UserContext);
  const { intelligenceRequests, getIntelligenceRequests }
    = useIntelligenceRequests();
  const [selectedIntelligenceRequest, setSelectedIntelligenceRequest]
    = useState<number | null>(null);

  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedRequest = useMemo(
    () =>
      intelligenceRequests.find(
        req => Number(req.id) === selectedIntelligenceRequest,
      ) || null,
    [intelligenceRequests, selectedIntelligenceRequest],
  );

  const ratingValues = useMemo<RatingValues>(() => {
    return mapRatingsArray(currentReview?.ratings as any);
  }, [currentReview?.ratings]);

  const [formFields, extendFormFields, setFormFields]
    = useExtendState<BasicReview>(
      editMode && defaultValues
        ? (defaultValues as BasicReview)
        : (currentReview ?? ({} as BasicReview)),
    );

  const [ratingFormFields, setRatingFormFields] = useState<RatingValues>(
    editMode && defaultValues
      ? {
          transparency: defaultValues.transparency ?? 0,
          clarity: defaultValues.clarity ?? 0,
          responsiveness: defaultValues.responsiveness ?? 0,
          understanding: defaultValues.understanding ?? 0,
        }
      : ratingValues,
  );

  const [newCompany, setNewCompany] = useState({
    companyName: companyName || '',
    stockTicker: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { errors, validateFields, validateField } = useValidation(
    { ...formFields, ...ratingFormFields },
    BASIC_REVIEW_FORM_FIELDS,
  );

  const postReviewSteps = (
    isReviewDeepDive: boolean,
    companyId: number,
    handleReviewSuccess: (showSnackbar: boolean, redirectPath?: string) => void,
    fetchAllReviews: (companyId: number) => void,
    createdCompanyName?: string,
  ) => {
    if (isReviewDeepDive) {
      setReviewStep(1);
    }
    else {
      fetchAllReviews(companyId);
      if (isAddCompany && createdCompanyName) {
        handleReviewSuccess(true, `/company/${encodeURIComponent(createdCompanyName)}`);
      }
      else {
        handleReviewSuccess(true);
      }
    }
  };

  const handleRatingChange = (name: string, value: number | null) => {
    setRatingFormFields({
      ...ratingFormFields,
      [name]: value || 0,
    });
    validateField(name, value);
  };

  const submitBasicReviewType = async (isReviewDeepDive?: boolean) => {
    if (isSubmitting) {
      return;
    }

    const hasErrors = validateFields();
    if (!user?.idToken || hasErrors) {
      return;
    }

    setIsSubmitting(true);

    try {
      // If reviewId already exists, just navigate forward without creating a new review
      if (reviewId) {
        postReviewSteps(
          isReviewDeepDive || false,
          companyId as number,
          handleReviewSuccess ?? (() => {}),
          () =>
            fetchFilteredReviews({
              searchType: 'custom',
              companyId: companyId as number,
            }),
          companyName,
        );
        setIsSubmitting(false);
        return;
      }

      const createdCompany = isAddCompany
        ? await createCompanyFromReview(newCompany, user?.idToken)
        : null;

      const reviewInput = {
        ...formFields,
        reviewType: 'OWNED',
        reviewDepth:
          currentReview?.reviewDepth || isReviewDeepDive
            ? ReviewDepth.DEEP_DIVE
            : ReviewDepth.BASIC,
        userId: user?.id,
        companyId: createdCompany?.id ?? companyId ?? 0,
        companyName: createdCompany?.companyName || companyName,
        key: ReviewStep.BASIC_REVIEW,
      };

      const createdBasicReviewId = await createBasicReview(
        reviewInput,
        user?.idToken,
      );

      setReviewId(createdBasicReviewId);

      const ratingsInput: RatingsInput = {
        companyId: createdCompany?.id ?? companyId ?? 0,
        reviewId: createdBasicReviewId,
        ratings: {
          transparency: ratingFormFields.transparency,
          clarity: ratingFormFields.clarity,
          responsiveness: ratingFormFields.responsiveness,
          understanding: ratingFormFields.understanding,
        },
      };

      await createBasicReviewRatings(ratingsInput, user?.idToken);

      postReviewSteps(
        isReviewDeepDive || false,
        (createdCompany?.id ?? companyId) as number,
        handleReviewSuccess ?? (() => {}),
        () =>
          fetchFilteredReviews({
            searchType: 'custom',
            companyId: (createdCompany?.id ?? companyId) as number,
          }),
        createdCompany?.companyName,
      );
      setFormFields({} as BasicReview);
    }
    catch (error) {
      console.error('Error submitting basic review:', error);
      setIsSubmitting(false);
      if (cancelReview) {
        cancelReview();
      }
    }
  };

  const editReview = async (
    reviewId: number,
    fields: BasicReview,
  ): Promise<void> => {
    const hasErrors = validateFields();
    if (hasErrors) {
      return;
    }

    await api.patch(`/review/${reviewId}/edit`, {
      reviewId,
      data: fields,
      step: ReviewStep.BASIC_REVIEW,
      isAdmin: user?.isAdmin,
    });
    handleEditSuccess && handleEditSuccess();
  };

  const handleCustomSubmit = () => {
    submitBasicReviewType(true);
  };

  const handleCustomEdit = () => {
    if (defaultValues?.id) {
      editReview(defaultValues.id, {
        ...formFields,
        ...ratingFormFields,
      });
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (formFields.aiInvolved === 'Yes') {
      searchParams.set('showAiQuestion', 'true');
    }
    else {
      searchParams.delete('showAiQuestion');
    }
    navigate({ search: searchParams.toString() }, { replace: true });
  }, [formFields.aiInvolved, location.search, navigate]);

  useEffect(() => {
    getIntelligenceRequests({
      status: [IntelligenceRequestStatus.ACTIVE],
      companyId: [companyId],
    });
  }, [companyId]);

  useEffect(() => {
    if (setRfp) {
      setRfp(formFields.dealRfp === 'Yes');
    }
  }, [formFields.dealRfp, setRfp]);

  useEffect(() => {
    if (editMode) {
      setFormFields(defaultValues as BasicReview);
    }
    else {
      setFormFields(currentReview ?? ({} as BasicReview));
    }
  }, [currentReview, editMode]);

  useEffect(() => {
    if (editMode) {
      setRatingFormFields({
        transparency: defaultValues?.transparency ?? 0,
        clarity: defaultValues?.clarity ?? 0,
        responsiveness: defaultValues?.responsiveness ?? 0,
        understanding: defaultValues?.understanding ?? 0,
      });
    }
    else {
      setRatingFormFields(ratingValues);
    }
  }, [ratingValues, editMode, defaultValues]);

  // Header section with review guidance and other pre-form content
  const headerSection = (
    <>
      {!editMode && (
        <InfoBar data-testid="basic-review-step-content-anchor">
          <SectionTitle>Deal Review Guidance</SectionTitle>

          <TextContainer>
            <SectionSubtext>
              Thank you for sharing with Kandir.io! Here are a few tips to
              ensure your review is approved & valuable.
            </SectionSubtext>
            <ul>
              <li>
                <strong>Take Care:</strong>
                {' '}
                Deal Reviews should typically take 15 to
                20 minutes. If it takes 5 minutes, you're going too fast or not
                providing enough detail to make it useful!
              </li>
              <li>
                <strong>Empathize:</strong>
                {' '}
                Put yourself in the shoes of future
                sellers selling into this account - what should they know to get
                the best result?
              </li>
              <li>
                <strong>Avoid Generic Feedback:</strong>
                {' '}
                comments like "know
                your champion" or "be persistent" aren't helpful unless you can
                explain why these matter more than usual
                <i>specifically for this account.</i>
              </li>
            </ul>
          </TextContainer>
        </InfoBar>
      )}

      {!editMode && (
        <ActiveReviewIntelligenceRequests
          companyId={companyId ?? 0}
          extendReview={extendFormFields}
          selectedId={selectedIntelligenceRequest}
          setSelectedId={setSelectedIntelligenceRequest}
          intelligenceRequests={intelligenceRequests}
        />
      )}

      {isAddCompany && (
        <AddCompanyForm
          newCompany={newCompany}
          setNewCompany={setNewCompany}
          errors={errors}
        />
      )}
    </>
  );

  // Custom question renderer that includes the standard questions plus ratings
  const customQuestionRenderer = (
    <>
      {/* Render multiple choice questions */}
      {BASIC_REVIEW_FORM_FIELDS.filter(
        (q): q is MultipleChoiceQuestion =>
          q.type === QuestionType.MULTIPLE_CHOICE,
      ).map(question => (
        <MultipleChoiceReviewQuestion
          key={question.name}
          question={question}
          disabledOptions={getDisabledOptions(question.name, selectedRequest)}
          formFields={formFields}
          handleFormFieldChange={(e) => {
            extendFormFields({ [question.name]: e.currentTarget.value });
            validateField(question.name, e.currentTarget.value);
          }}
          errors={errors}
          isNarrow={question.options.length > 8}
        />
      ))}

      {/* Render text questions */}
      <Grid container spacing={2}>
        {BASIC_REVIEW_FORM_FIELDS.filter(
          (q): q is TextQuestion => q.type === QuestionType.TEXT,
        ).map((question, i) => (
          <ReviewTextArea
            key={question.name}
            question={question}
            formFields={formFields}
            handleFormFieldChange={(e) => {
              extendFormFields({ [question.name]: e.currentTarget.value });
              validateField(question.name, e.currentTarget.value);
            }}
            errors={errors}
            includeTextLimitValidation
            index={i}
            helperText={question.helperText}
          />
        ))}

        {/* Ratings section */}
        <P
          style={{
            margin: '0 var(--spacing-md)',
            marginTop: 'var(--spacing-md)',
            fontWeight: 600,
          }}
        >
          On a scale of 1 - 5, how would you rate the companies:
        </P>
        <Box
          sx={{
            padding: 'var(--spacing-md)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--spacing-md)',
            width: '100%',
            [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
              flexDirection: 'column',
            },
          }}
        >
          {BASIC_REVIEW_FORM_FIELDS.filter(
            (r): r is RatingQuestion => r.type === QuestionType.RATING,
          ).map(question => (
            <ReviewRatingQuestion
              handleRatingChange={handleRatingChange}
              key={question.name}
              disabled={editMode}
              question={question}
              formFields={ratingFormFields}
              currentReview={currentReview}
              errors={errors}
            />
          ))}
        </Box>
      </Grid>
    </>
  );

  // Footer section with custom buttons
  const footerSection = editMode
    ? (
        <SaveBar>
          <SaveButton onClick={handleCustomEdit}>
            {`Save ${getReviewStepName(ReviewStep.BASIC_REVIEW)} Changes`}
          </SaveButton>
        </SaveBar>
      )
    : (
        <CenteredDiv>
          <SaveButton
            onClick={() => {
              submitBasicReviewType(true);
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Continue to Entry Strategy'}
          </SaveButton>
        </CenteredDiv>
      );

  return (
    <BaseReviewForm
      reviewStep={ReviewStep.BASIC_REVIEW}
      formFields={BASIC_REVIEW_FORM_FIELDS}
      editMode={editMode}
      defaultValues={defaultValues}
      onSubmit={handleCustomSubmit}
      onEditSuccess={handleCustomEdit}
      customValidationFields={ratingFormFields}
      disabledOptionsGetter={(questionName: string) =>
        getDisabledOptions(questionName, selectedRequest)}
      headerSection={headerSection}
      customQuestionRenderer={customQuestionRenderer}
      footerSection={footerSection}
      hideDefaultButtons={true}
    />
  );
};

export default BasicReviewForm;
